import { Head } from "@inertiajs/react";
import MarketingLayout from "@/layouts/MarketingLayout";
import SiteNav from "@/shared/marketing/SiteNav";
import PageHero from "@/shared/marketing/PageHero";
import { type ReactNode } from "react";

const BC = { fontFamily: "'Oswald', sans-serif" } as const;

// ── Section divider ────────────────────────────────────────────────────────────
function SectionHead({ label, id }: { label: string; id?: string }) {
  return (
    <div id={id} className="flex items-center gap-4 px-6 md:px-10 pt-14 pb-6 max-w-[1200px] mx-auto">
      <span className="text-[#ff2e89] text-[0.78rem] font-bold tracking-[0.32em] uppercase whitespace-nowrap" style={BC}>
        {label}
      </span>
      <div className="flex-1 h-px bg-[#242424]" />
    </div>
  );
}

// ── Feature row ────────────────────────────────────────────────────────────────
interface FeatureRowProps {
  tag: string;
  title: ReactNode;
  body: string;
  bullets: string[];
  visualLabel: ReactNode;
  gradient: string;
  flip?: boolean;
}

function FeatureRow({ tag, title, body, bullets, visualLabel, gradient, flip = false }: FeatureRowProps) {
  const visual = (
    <div
      className="relative overflow-hidden min-h-[320px] md:min-h-auto"
      style={{ background: gradient }}
    >
      {/* Pink top accent */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#ff2e89]" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="text-transparent text-center leading-tight select-none"
          style={{ ...BC, fontSize: "clamp(3rem, 6vw, 5.5rem)", fontWeight: 700, WebkitTextStroke: "1px rgba(255,46,137,0.18)" }}
        >
          {visualLabel}
        </span>
      </div>
    </div>
  );

  const text = (
    <div
      className="flex flex-col justify-center p-8 md:p-12 border border-[#242424]"
      style={{ background: "#141414", borderLeft: flip ? "1px solid #242424" : "none", borderRight: flip ? "none" : undefined }}
    >
      <p className="text-[#ff2e89] text-[0.72rem] font-bold tracking-[0.28em] uppercase mb-3" style={BC}>{tag}</p>
      <h2
        className="text-[#f0ece8] font-black uppercase leading-none mb-5"
        style={{ ...BC, fontSize: "clamp(1.8rem, 3vw, 2.5rem)" }}
      >
        {title}
      </h2>
      <p className="text-[#888] text-[0.95rem] leading-[1.75] mb-6 max-w-[44ch]">{body}</p>
      <ul className="list-none p-0 m-0 flex flex-col gap-2.5">
        {bullets.map((b) => (
          <li key={b} className="flex items-center gap-2.5 text-sm text-[#888]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ff2e89] shrink-0" />
            {b}
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2">
      {flip ? <>{text}{visual}</> : <>{visual}{text}</>}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Organizers() {
  return (
    <>
      <Head title="Driftables — For Organizers" />

      <SiteNav />

      <PageHero
        eyebrow="For Track Managers & Event Hosts"
        titleSolid="Run Your Event."
        titleOutline="Your Way."
        subtitle="Driftables gives organizers the tools to manage every part of an event — applications, invitations, check-in, and history — all in one place. Spend less time on admin. Spend more time on track."
        primaryCta={{ label: "Get Started Free", href: "/users/sign_up" }}
        secondaryCta={{ label: "See How It Works", href: "#how-it-works" }}
        ghostText="MANAGE"
        titleSize="clamp(4rem, 10vw, 8.5rem)"
      />

      {/* ── HOW IT WORKS ── */}
      <SectionHead label="How It Works" id="how-it-works" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-[3px] px-6 md:px-10 max-w-[1200px] mx-auto">
        {/* Step 1 — rendered manually for the mailto link */}
        <div className="bg-[#141414] border border-[#242424] p-10">
          <span
            className="block leading-none mb-6 text-transparent"
            style={{ ...BC, fontSize: "5rem", fontWeight: 900, WebkitTextStroke: "1px rgba(255,46,137,0.25)" }}
          >
            01
          </span>
          <h3 className="text-[#f0ece8] text-[1.4rem] font-bold uppercase tracking-[0.05em] mb-3" style={BC}>
            Create Your Account
          </h3>
          <p className="text-[#888] text-[0.925rem] leading-[1.65]">
            Sign up for free, then{" "}
            <a
              href="mailto:driftablesdotnet@gmail.com"
              className="no-underline border-b border-[rgba(255,46,137,0.4)] pb-px text-[#ff2e89] hover:border-[#ff2e89] transition-colors"
            >
              email us to get your account connected
            </a>{" "}
            — we verify organizers manually so we know you're actually running a track. Link your track or club so participants can find your events on the Driftables directory.
          </p>
        </div>

        {/* Steps 2 & 3 */}
        {[
          {
            num: "02",
            title: "Create an Event",
            body: "Set your date, location, and which participant types you're accepting — drivers, media, staff, teams, or any combination. Your event goes live on the Driftables calendar.",
          },
          {
            num: "03",
            title: "Manage & Run It",
            body: "Review applications, send invitations, assign roles, and check people in on the day. Every attendee is logged in your event history — forever.",
          },
        ].map(({ num, title, body }) => (
          <div key={num} className="bg-[#141414] border border-[#242424] p-10">
            <span
              className="block leading-none mb-6 text-transparent"
              style={{ ...BC, fontSize: "5rem", fontWeight: 900, WebkitTextStroke: "1px rgba(255,46,137,0.25)" }}
            >
              {num}
            </span>
            <h3 className="text-[#f0ece8] text-[1.4rem] font-bold uppercase tracking-[0.05em] mb-3" style={BC}>
              {title}
            </h3>
            <p className="text-[#888] text-[0.925rem] leading-[1.65]">{body}</p>
          </div>
        ))}
      </div>

      {/* ── FEATURES ── */}
      <SectionHead label="Features" id="features" />

      <div className="flex flex-col gap-[3px] px-6 md:px-10 max-w-[1200px] mx-auto">
        <FeatureRow
          tag="Applications"
          title={<>One Dashboard.<br />Every Role.</>}
          body="Driver applications, media credentials, staff assignments, and team requests all land in the same place. Filter by role, review profiles, and approve or decline with one click."
          bullets={[
            "Separate queues for drivers, media, staff, and teams",
            "Applicant profiles with contact info and history",
            "Automatic notifications to applicants",
          ]}
          visualLabel={<>APPLI-<br />CATIONS</>}
          gradient="linear-gradient(135deg, #1a0d14 0%, #0d0814 50%, #100d1a 100%)"
        />
        <FeatureRow
          tag="Invitations"
          title={<>Invite Who<br />You Want.</>}
          body="Running an invitational? Send direct invitations to specific drivers, media, or teams — no open application needed. They get a link, they accept, they're in."
          bullets={[
            "Invite by name, email, or past attendee history",
            "Set expiry dates on invitations",
            "Mix open applications with private invitations",
            "Invitees bypass the public application queue",
          ]}
          visualLabel={<>INVITE<br />ONLY</>}
          gradient="linear-gradient(135deg, #0d1a10 0%, #081408 50%, #0a1010 100%)"
          flip
        />
        <FeatureRow
          tag="History"
          title={<>Know Your<br />People.</>}
          body="Every event you run builds a permanent record of who was there and in what role. Look back at any past event and see exactly who attended — useful for insurance, sponsorships, and relationships."
          bullets={[
            "Full attendee log for every event",
            "Filter history by role, date, or participant",
            "See a participant's full history across your events",
          ]}
          visualLabel="HISTORY"
          gradient="linear-gradient(135deg, #1a1408 0%, #14100a 50%, #1a0d08 100%)"
        />
        <FeatureRow
          tag="Public Pages"
          title={<>Your Event.<br />Your Brand.</>}
          body="Every event gets a public-facing page on Driftables — visible to the whole community. Drivers and media can find it, share it, and apply directly. You control what's visible and when."
          bullets={[
            "Event detail page with date, location, and description",
            "Listed on the Driftables events calendar",
            "Control when applications open and close",
            "Post-event: page shows who attended",
          ]}
          visualLabel={<>YOUR<br />PAGE</>}
          gradient="linear-gradient(135deg, #0d1018 0%, #080c14 50%, #0a0d1a 100%)"
          flip
        />
      </div>

      {/* ── CTA BANNER ── */}
      <div className="px-6 md:px-10 max-w-[1200px] mx-auto mt-[3px] mb-0">
        <div
          className="relative overflow-hidden border border-[#242424] flex items-center justify-between gap-8 flex-wrap px-10 md:px-14 py-16"
          style={{ background: "#141414" }}
        >
          {/* Pink top line */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#ff2e89]" />
          {/* Ghost text */}
          <span
            className="absolute right-[-0.05em] bottom-[-0.1em] text-transparent pointer-events-none select-none leading-none hidden md:block"
            style={{ ...BC, fontSize: "14rem", fontWeight: 900, WebkitTextStroke: "1px rgba(255,46,137,0.06)" }}
          >
            GO
          </span>

          <div className="relative z-10">
            <p className="text-[#ff2e89] text-[0.78rem] font-bold tracking-[0.3em] uppercase mb-3" style={BC}>
              Ready to get started?
            </p>
            <h2
              className="text-[#f0ece8] font-black uppercase leading-[0.95]"
              style={{ ...BC, fontSize: "clamp(2rem, 4vw, 3rem)" }}
            >
              Your next event<br />starts here.
            </h2>
          </div>

          <div className="flex gap-4 flex-wrap relative z-10">
            <a
              href="/users/sign_up"
              className="no-underline inline-block uppercase font-bold tracking-widest text-sm bg-[#ff2e89] text-[#0b0b0b] px-8 py-3 hover:opacity-80 transition-opacity"
              style={BC}
            >
              Create Free Account
            </a>
            <a
              href="/events"
              className="no-underline inline-block uppercase font-bold tracking-widest text-sm border border-[#242424] text-[#f0ece8] px-8 py-3 hover:border-[rgba(240,236,232,0.4)] transition-colors"
              style={BC}
            >
              Browse Events First
            </a>
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="border-t border-[#242424] px-6 md:px-10 py-7 mt-16 flex flex-col md:flex-row items-center justify-between gap-2">
        <span className="text-[#555] text-sm font-black tracking-widest uppercase" style={BC}>
          Drift<span className="text-[#ff2e89] font-black" style={BC}>ables</span>
        </span>
        <span className="text-[#555] text-xs">&copy; 2026 Driftables. All rights reserved.</span>
      </footer>
    </>
  );
}

Organizers.layout = (page: ReactNode) => <MarketingLayout>{page}</MarketingLayout>;
